import { CustomerAttribute, CustomerAttributeGroup } from '@common/customerAttributes/types';
import { FeatureType } from '@src/defs/features';
import { AuthState } from '@src/store/auth';
import { ConfigState } from '@src/store/config';
import {
  ApiLoadConsentResponse, ConsentSaveType, ConsentType,
} from '@src/types/common';
import { mapConsentFeaturesTo } from '@src/util/consents';
import { getTracingHeaders } from '@src/util/helpers';
import { ClientAbstract } from './ClientAbstract';
import { HttpClient } from './HttpClient';

type Consents4p = {
  id: string;
  status: 'revoked' | 'active';
};

type ConsentFeatureSplit = [string, FeatureType, string];

export const mapCustomerAttributeTypeToSaveType = (
  customerAttribute : CustomerAttribute,
  spId: string,
  lcId: string,
) => {
  let customerAttributeItems : Consents4p[] = [];

  ['standard', 'extra'].forEach((customerAttributeGroup) => {
    const groupItems = customerAttribute[customerAttributeGroup] as CustomerAttributeGroup;
    if (groupItems) {
      const items = groupItems.items.map((item) => {
        // All items that have granted=undefined must be send as status=active to the backend.
        // The backend will check if any consent is set for a customer attribute and will set the correct
        // value to be saved in the DB.
        let itemStatus = item.granted ? 'active' : 'revoked';
        if (typeof item.granted === 'undefined') {
          itemStatus = 'active';
        }

        return {
          id: `cattr:${spId}:${lcId}:${customerAttribute.id}:${item.id}`,
          status: itemStatus,
        } as Consents4p;
      });
      customerAttributeItems = [...customerAttributeItems, ...items];
    }
  });

  return customerAttributeItems;
};

export class Client4p extends ClientAbstract {
  private readonly httpClient4p: HttpClient;

  constructor(config: ConfigState, auth: AuthState) {
    super(config, auth);
    const { apiUri4p, accesstoken4p, traceid } = config;

    const headers = {
      Accept: 'application/json; charset=utf-8',
      ...(accesstoken4p ? { Authorization: `Bearer ${accesstoken4p}` } : {}),
      ...getTracingHeaders(traceid),
    };

    this.httpClient4p = new HttpClient(apiUri4p!, headers);
  }

  async getConsents(lifecycle: string) {
    const { spId, lcId } = this.getCustomerInfoFromConfigOrAuth();

    const path = lcId
      ? `/consent/sp/${spId}/customer/${lcId}/ui-aspects`
      : `/consent/sp/${spId}/register`;

    const qs = {
      lifecycle,
      textformat: 'html',
      ...(lcId ? { ignoreNotFound: true } : {}),
      additionalInfo: true,
      pageformat: true,
      includeCustomerAttributes: true,
    };

    const uiAspects = await this.httpClient.get<ApiLoadConsentResponse>(path, qs);
    const consents = await this.httpClient4p.get<Consents4p[]>(
      '/consents',
      { purpose_levels: 'consent,legitimate_interest' },
    );

    consents.forEach(({ id: uniqId, status }) => {
      this.grantOrRevokeCidConsents(uiAspects, uniqId, status);
      this.grantOrRevokeCattrConsents(uiAspects, uniqId, status);
    });

    return uiAspects;
  }

  async saveConsents(consentData: ConsentSaveType[], customerAttributesData: CustomerAttribute[] = []) {
    const {
      channel = 'Web', lifecycle, usertype, references,
    } = this.config;
    let mappedConsents: Consents4p[] = [];
    const coreConsentMappingPrefix = this.getCoreConsentMappingPrefix();
    const { spId, lcId } = this.getCustomerInfoFromConfigOrAuth();

    consentData.forEach((cons) => {
      const { id, granted } = cons;
      mappedConsents.push({
        id: `${coreConsentMappingPrefix}${id}`,
        status: granted ? 'active' : 'revoked',
      });

      const featuresMapped = mapConsentFeaturesTo<Consents4p>(
        cons,
        ({ id: fid, granted: fgranted = false }, featureName) => ({
          id: `${coreConsentMappingPrefix}${id}:${featureName}:${fid}`,
          status: fgranted ? 'active' : 'revoked',
        }),
      );

      if (Object.keys(featuresMapped).length > 0) {
        mappedConsents.push(
          ...(featuresMapped[Object.keys(featuresMapped)[0] as FeatureType]
            || []),
        );
      }
    });

    // Collect optional customer attributes
    const customerAttributes = {};
    if (customerAttributesData.length > 0) {
      const mappedCustomerAttributes = customerAttributesData.flatMap(
        (customerAttributeData) => mapCustomerAttributeTypeToSaveType(customerAttributeData, spId, lcId!),
      );

      mappedConsents = mappedConsents.concat(mappedCustomerAttributes);
    }

    await this.httpClient4p.post('/consents/update-list', {
      consents: mappedConsents,
      ...customerAttributes,
      triggered_by: JSON.stringify({ // WORKAROUND: WPCSCM-533
        lifecycle, // lifecycle is required, the rest is optional...
        ...(usertype ? { usertype } : {}),
        ...(references ? { references } : {}), // this is a JSON-stringified string !!
        ...(channel ? { channel } : {}),
      }),
    });
  }

  private grantOrRevokeCidConsents(uiAspects : ApiLoadConsentResponse, uniqId: string, status : string) {
    const coreConsentMappingPrefix = this.getCoreConsentMappingPrefix();
    const uniqIdWithoutPrefix = uniqId.substring(coreConsentMappingPrefix.length);
    const granted = status === 'active';
    const [id, featureType, featureId] = uniqIdWithoutPrefix.split(':') as ConsentFeatureSplit;

    let consent: ConsentType | undefined;
    // eslint-disable-next-line sonarjs/no-ignored-return
    uiAspects.groups.find((consentGroup) => {
      consent = consentGroup.consents.find((c) => c.id === id);
      return !!consent;
    });

    if (consent) {
      if (!featureType) {
        consent.granted = granted;
      } else {
        const feature = consent[featureType]?.find((f) => f.id === featureId);
        if (feature) {
          feature.granted = granted;
        }
      }
    }
  }

  private grantOrRevokeCattrConsents(uiAspects : ApiLoadConsentResponse, uniqId: string, status : string) {
    // eslint-disable-next-line sonarjs/unused-named-groups,sonarjs/slow-regex
    const cattrMatch = /^cattr:(?<spId>.*?):(?<lcId>.*?):(?<cattrGroup>.*?):(?<cattrId>.*)$/.exec(uniqId);

    if (cattrMatch) {
      const customerAttribute = uiAspects.customerAttributes!.find(
        (cattr) => cattr.id === cattrMatch.groups!.cattrGroup,
      );

      if (!customerAttribute) {
        return;
      }

      let customerAttributeItem = null;

      // Look in "extra", then "standard"
      if (customerAttribute.extra) {
        customerAttributeItem = customerAttribute.extra.items.find(
          (cattrItem) => cattrItem.id === cattrMatch.groups!.cattrId,
        );
      }

      if (!customerAttributeItem) {
        customerAttributeItem = customerAttribute.standard.items.find(
          (cattrItem) => cattrItem.id === cattrMatch.groups!.cattrId,
        );
      }

      if (customerAttributeItem) {
        customerAttributeItem.granted = status === 'active';
      }
    }
  }

  private getCoreConsentMappingPrefix() {
    const { spId, lcId } = this.getCustomerInfoFromConfigOrAuth();
    return `cid:${spId}:${lcId}:`;
  }
}
